import { createTheme, ThemeOptions } from "@mui/material/styles";

// 浅色主题配置
export const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      light: "#26d0ce",
      main: "#1a2980",
      dark: "#0f1a5c",
      contrastText: "#FFFFFF",
    },
    secondary: {
      light: "#FFD54F",
      main: "#FFC107",
      dark: "#FFA000",
      contrastText: "#212121",
    },
    success: {
      light: "#81C784",
      main: "#4CAF50",
      dark: "#388E3C",
      contrastText: "#FFFFFF",
    },
    warning: {
      light: "#FFB74D",
      main: "#FF9800",
      dark: "#F57C00",
      contrastText: "#212121",
    },
    error: {
      light: "#E57373",
      main: "#F44336",
      dark: "#D32F2F",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#f8fafc",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1e293b",
      secondary: "#64748b",
      disabled: "#94a3b8",
    },
    divider: "#e2e8f0",
  },
  typography: {
    fontFamily:
      '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body1: {
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      fontSize: "0.875rem",
      textTransform: "none",
    },
    caption: {
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 1px 2px rgba(0, 0, 0, 0.05)",
    "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
    "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",
    ...Array(19).fill("none"),
  ] as any,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 20px",
          fontWeight: 600,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            boxShadow: "0px 6px 12px rgba(26, 41, 128, 0.25)",
            transform: "translateY(-2px)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #0f1a5c 0%, #1fb8b6 100%)",
            boxShadow: "0px 8px 16px rgba(26, 41, 128, 0.3)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.12)",
            transform: "translateY(-4px)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)",
          boxShadow: "0px 4px 12px rgba(26, 41, 128, 0.2)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          borderRadius: "0px !important",
          minHeight: "64px !important",
        },
      },
    },
  },
};

// 深色主题配置
export const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      light: "#4fc3f7",
      main: "#29b6f6",
      dark: "#0288d1",
      contrastText: "#FFFFFF",
    },
    secondary: {
      light: "#ffb74d",
      main: "#ff9800",
      dark: "#f57c00",
      contrastText: "#000000",
    },
    success: {
      light: "#81c784",
      main: "#4caf50",
      dark: "#388e3c",
      contrastText: "#FFFFFF",
    },
    warning: {
      light: "#ffb74d",
      main: "#ff9800",
      dark: "#f57c00",
      contrastText: "#000000",
    },
    error: {
      light: "#e57373",
      main: "#f44336",
      dark: "#d32f2f",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#0a0a0a",
      paper: "#151515",
    },
    text: {
      primary: "#f8fafc",
      secondary: "#94a3b8",
      disabled: "#475569",
    },
    divider: "#1e293b",
  },
  typography: {
    fontFamily:
      '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body1: {
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      fontSize: "0.875rem",
      textTransform: "none",
    },
    caption: {
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 1px 2px rgba(255, 255, 255, 0.05)",
    "0px 1px 3px rgba(255, 255, 255, 0.1), 0px 1px 2px rgba(255, 255, 255, 0.06)",
    "0px 4px 6px -1px rgba(255, 255, 255, 0.1), 0px 2px 4px -1px rgba(255, 255, 255, 0.06)",
    "0px 10px 15px -3px rgba(255, 255, 255, 0.1), 0px 4px 6px -2px rgba(255, 255, 255, 0.05)",
    "0px 20px 25px -5px rgba(255, 255, 255, 0.1), 0px 10px 10px -5px rgba(255, 255, 255, 0.04)",
    "0px 25px 50px -12px rgba(255, 255, 255, 0.25)",
    ...Array(19).fill("none"),
  ] as any,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 20px",
          fontWeight: 600,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            boxShadow: "0px 6px 12px rgba(41, 182, 246, 0.4)",
            transform: "translateY(-2px)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #29b6f6 0%, #0288d1 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #0288d1 0%, #01579b 100%)",
            boxShadow: "0px 8px 16px rgba(41, 182, 246, 0.5)",
          },
        },
        outlined: {
          borderColor: "#334155",
          color: "#f8fafc",
          "&:hover": {
            borderColor: "#475569",
            backgroundColor: "rgba(248, 250, 252, 0.08)",
          },
        },
        text: {
          color: "#f8fafc",
          "&:hover": {
            backgroundColor: "rgba(248, 250, 252, 0.08)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
          border: "1px solid #1e293b",
          backgroundColor: "#151515",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.5)",
            transform: "translateY(-4px)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "#151515",
        },
        elevation0: {
          backgroundColor: "#0a0a0a",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#0f172a",
          borderRight: "1px solid #1e293b",
          borderRadius: 0,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          borderRadius: "0px !important",
          minHeight: "64px !important",
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "rgba(41, 182, 246, 0.16)",
            "&:hover": {
              backgroundColor: "rgba(41, 182, 246, 0.24)",
            },
          },
          "&:hover": {
            backgroundColor: "rgba(248, 250, 252, 0.16)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e293b",
          color: "#f8fafc",
        },
        outlined: {
          borderColor: "#334155",
        },
      },
    },
  },
};

// 创建主题函数
export const createAppTheme = (mode: "light" | "dark") => {
  const themeOptions = mode === "light" ? lightThemeOptions : darkThemeOptions;
  return createTheme(themeOptions);
};

// 主题类型
export type ThemeMode = "light" | "dark";
